import React, { useState } from "react";
import cn from "classnames";
import { Input } from "antd";
import {
  DownOutlined,
  FilterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import useIsPhone from "../../hooks/useIsPhone";
import styles from "./styles.module.scss";

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

/** 32px search field of the client toolbars (220px, radius 10). */
export const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onChange,
  placeholder,
  className,
}) => (
  <Input
    type="search"
    placeholder={placeholder}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={cn(styles.search, className)}
    prefix={<SearchOutlined className={styles.searchIcon} />}
  />
);

/** A toolbar row: filters on the left, the rest wraps under them. */
export const Toolbar: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className={styles.toolbar}>{children}</div>;

/**
 * The selects under the search. A toolbar row on a wide screen; on a phone
 * they fold under a Filters button that counts the ones in use, and open
 * as a two-column grid.
 */
export const FilterPanel: React.FC<{
  /** How many of the filters differ from "all". */
  active: number;
  children: React.ReactNode;
}> = ({ active, children }) => {
  const isPhone = useIsPhone();
  const [open, setOpen] = useState(false);

  if (!isPhone) return <Toolbar>{children}</Toolbar>;

  return (
    <div className={styles.panel}>
      <button
        type="button"
        className={cn(styles.panelToggle, { [styles.panelOpen]: open })}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <FilterOutlined className={styles.panelIcon} />
        Filters
        {active > 0 && <span className={styles.panelCount}>{active}</span>}
        <DownOutlined className={styles.panelArrow} />
      </button>
      {open && <div className={styles.panelGrid}>{children}</div>}
    </div>
  );
};

export { styles as filterStyles };
